import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import moment from 'moment'

import styled from 'styled-components'
import { getFiltersFromBounds, LocationToLatLngBounds } from 'utils/geo'
import { serializeSearchState } from 'actions/utils/search'

import { boundsToFloat } from 'components/maps/Map/mapUtils'

import { defaultStateCodeForMarket } from '../../utils'
import { CallToActionButton } from '../../shared'
import FilterModal from './FilterModal'
import SelectLocation from './SelectLocation'
import ModalButton from './ModalButton'
import { Filters, Country, State, PrimaryCity } from '../types'

const SubmitButton = styled(CallToActionButton)`
  max-width: 100%;
  width: 100%;
  margin-top: 16px;
`

export const buildStateOptions = (
  states: Readonly<State[]>,
  primaryCity: PrimaryCity,
  marketCode: GQL_MarketCode
) => {
  const nearPrimaryCityOption = {
    name: `Near ${primaryCity?.name}`,
    code: 'primary_city',
  }

  const matchingStateCode = defaultStateCodeForMarket(marketCode)

  const stateMatchesMarket = ({ code }: State) => code === matchingStateCode
  const stateOutsideOfMarket = ({ code }: State) => code !== matchingStateCode

  return [
    ...(states.filter(stateMatchesMarket) || []), // first option -> state that matches the current market
    ...(primaryCity != null ? [nearPrimaryCityOption] : []), // second option -> primary city of current market
    ...(states.filter(stateOutsideOfMarket) || []), // other options -> other states in the country
  ]
}

export const generateParams = (filters: Filters, primaryCity?: PrimaryCity) => {
  const {
    country,
    state,
    startDate,
    endDate,
    adults,
    children,
    weekendMonths,
  } = filters

  const searchParams = new Map()

  searchParams.set('type', 'accommodation')
  searchParams.set('country', country)

  if (state === 'primary_city') {
    const { latitude, longitude } = primaryCity

    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    const bounds = LocationToLatLngBounds(
      {
        lat,
        lng,
      },
      200000, // 200 km
      google.maps.Circle
    )

    const { sw, ne } = getFiltersFromBounds(bounds)

    searchParams.set('sw[]', boundsToFloat(sw))
    searchParams.set('ne[]', boundsToFloat(ne))
  } else {
    searchParams.set('state', state || 'all')
  }

  if (startDate && endDate) {
    searchParams.set('startDate', moment(startDate).format('YYYY-MM-DD'))
    searchParams.set('endDate', moment(endDate).format('YYYY-MM-DD'))
    searchParams.delete('weekendMonths')
  }

  if (weekendMonths?.length > 0) {
    searchParams.set('weekendMonths', (weekendMonths as unknown) as string)
    searchParams.delete('startDate')
    searchParams.delete('endDate')
  }

  if (adults) {
    searchParams.set('adults', adults.toString())
  }

  if (children) {
    searchParams.set('children', children.toString())
  }

  return serializeSearchState(Object.fromEntries(searchParams))
}

interface HeaderSearchFormProps {
  marketCode?: GQL_MarketCode
  country?: Country
  countries: Country[]
  states: State[]
  primaryCity?: PrimaryCity
}

const SearchForm = ({
  marketCode,
  country,
  countries,
  states,
  primaryCity,
}: HeaderSearchFormProps) => {
  const history = useHistory()
  const stateOptions = useMemo(() => {
    return marketCode ? buildStateOptions(states, primaryCity, marketCode) : []
  }, [states, primaryCity, marketCode])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    weekendMonths: null,
    adults: 0,
    children: 0,
    country: country?.code || countries[0]?.code,
    state: marketCode ? stateOptions[0]?.code : null,
  })

  const onDatesChange = useCallback(
    ({ startDate, endDate }: Pick<Filters, 'startDate' | 'endDate'>) => {
      setFilters((currentFilters) => ({
        ...currentFilters,
        startDate,
        endDate,
        weekendMonths: null,
      }))
    },
    []
  )

  const onWeekendMonthsChange = useCallback((weekendMonths: string[]) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      weekendMonths,
      startDate: null,
      endDate: null,
    }))
  }, [])

  const onFilterChange = useCallback(
    (key: string, value: string | number) => {
      setFilters({
        ...filters,
        [key]: value,
      })
    },
    [filters]
  )

  const onSubmit = useCallback(() => {
    const pathname = `/listings`
    const searchParams = generateParams(filters, primaryCity)

    history.push(
      {
        pathname,
        search: searchParams.toString(),
      },
      {
        primaryCityName: primaryCity?.name,
      }
    )

    window.scrollTo(0, 0)
  }, [filters])

  return (
    <>
      <SelectLocation
        onFilterChange={onFilterChange}
        value={marketCode ? filters.state : filters.country}
        type={marketCode ? 'state' : 'country'}
        options={marketCode ? stateOptions : countries}
      />
      <ModalButton filters={filters} onClick={() => setFilterModalOpen(true)} />
      <FilterModal
        filters={filters}
        open={filterModalOpen}
        onDatesChange={onDatesChange}
        onWeekendMonthsChange={onWeekendMonthsChange}
        onFilterChange={onFilterChange}
        onClose={() => setFilterModalOpen(false)}
        onSubmit={onSubmit}
      />
      <SubmitButton onClick={onSubmit} rounded>
        Search escapes
      </SubmitButton>
    </>
  )
}

export default SearchForm
